import { fetchBaseQuery as buildFetchBaseQuery } from '@reduxjs/toolkit/query';
import { type BaseFetchQueryFn, type BaseFetchQueryFnArgs } from 'ReduxHelpers';
import { convertQueryCasing } from './convertCasing';
import { retryNetworkErrors } from './retryNetworkErrors';
import { formatErrorResponse } from './formatErrorResponse';
import { baseUrl, serializeParams, buildAuthHeaders } from './queryShared';

export const keepUnusedDataFor = process.env.NODE_ENV !== 'test' ? 60 : 0;

export function buildRtkQueryBaseBackRoyalQueryFn(
    apiName: string,
    { timeout = 30000 }: { timeout?: number } = {},
): BaseFetchQueryFn<unknown> {
    // setup base configuration
    const configuredQuery: BaseFetchQueryFn<unknown> = buildFetchBaseQuery({
        baseUrl,
        prepareHeaders: headers => buildAuthHeaders(headers),
        paramsSerializer: serializeParams,
        timeout,
    });

    // camelcase the arguments going into the query and the result coming out
    const queryWithConvertCasing: BaseFetchQueryFn<unknown> = (args, api, extraOptions = {}) =>
        convertQueryCasing(args, (convertedArgs: BaseFetchQueryFnArgs) =>
            configuredQuery(convertedArgs, api, extraOptions),
        );

    // format errors so that fetch errors and parse errors are in the same format as errors from
    // our api (as much as possible)
    const queryWithFormattedErrors: BaseFetchQueryFn<unknown> = formatErrorResponse({
        apiName,
        query: (args, api, extraOptions) => queryWithConvertCasing(args, api, extraOptions),
    });

    // implement retry logic. This has to come after formatErrorResponse, because it requires the
    // formatted error
    const queryWithRetry: BaseFetchQueryFn<unknown> = retryNetworkErrors((args, api, extraOptions) =>
        queryWithFormattedErrors(args, api, extraOptions),
    );

    return queryWithRetry;
}
